import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Edit2, Users } from "lucide-react";
import { toast } from "sonner";
import { inputCls, StatusBadge, fmtMoney } from "@/components/admin/FormBits";
import EmployeeForm from "./EmployeeForm";

export const DEPARTMENTS = ["Operations", "Production", "ICT", "Software", "Sales & Marketing", "Finance", "HR", "Administration"];

export default function AdminEmployees() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [dept, setDept] = useState("");
    const [search, setSearch] = useState("");

    function load() { api.get("/admin/employees").then((r) => setItems(r.data)).catch(() => {}); }
    useEffect(load, []);

    async function save(payload) {
        try {
            if (payload.id) { await api.put(`/admin/employees/${payload.id}`, payload); toast.success("Employee updated"); }
            else { await api.post("/admin/employees", payload); toast.success("Employee added"); }
            setEditing(null); load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }

    async function remove(id) {
        if (!confirm("Delete this employee record?")) return;
        await api.delete(`/admin/employees/${id}`); toast.success("Deleted"); load();
    }

    const visible = items.filter((e) => (!dept || e.department === dept) && `${e.full_name} ${e.staff_no} ${e.position} ${e.email}`.toLowerCase().includes(search.toLowerCase()));
    const active = items.filter((e) => e.status === "active").length;
    const payroll = items.filter((e) => e.status !== "terminated").reduce((s, e) => s + Number(e.salary || 0), 0);

    return (
        <div className="p-8" data-testid="admin-employees">
            <div className="flex items-center justify-between mb-1">
                <h1 className="font-display text-3xl font-bold">Employees</h1>
                <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 font-semibold" data-testid="add-employee-btn">
                    <Plus className="w-4 h-4" /> Add employee
                </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Staff bio-data, contacts, roles and next of kin.</p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <Kpi label="Total staff" value={items.length} testId="emp-kpi-total" />
                <Kpi label="Active" value={active} testId="emp-kpi-active" />
                <Kpi label="Monthly payroll (UGX)" value={fmtMoney(payroll)} testId="emp-kpi-payroll" />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff…" className={`${inputCls} max-w-xs`} data-testid="employee-search" />
                <select value={dept} onChange={(e) => setDept(e.target.value)} className={`${inputCls} max-w-[200px]`} data-testid="employee-dept-filter">
                    <option value="">All departments</option>{DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
                </select>
            </div>

            <div className="bg-white border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted text-left"><tr>
                        <th className="px-4 py-3">Staff</th><th className="px-4 py-3">Department / Role</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Hired</th><th className="px-4 py-3 text-right">Salary</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {visible.map((e) => (
                            <tr key={e.id} className="border-t border-border" data-testid={`employee-row-${e.id}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        {e.photo_url ? <img src={e.photo_url} alt="" className="w-9 h-9 object-cover rounded-full" /> : <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"><Users className="w-4 h-4 text-muted-foreground" /></div>}
                                        <div><div className="font-semibold">{e.full_name}</div><div className="text-xs font-mono text-muted-foreground">{e.staff_no}</div></div>
                                    </div>
                                </td>
                                <td className="px-4 py-3"><div>{e.department}</div><div className="text-xs text-muted-foreground">{e.position} · {e.employment_type}</div></td>
                                <td className="px-4 py-3 text-xs"><div>{e.phone}</div><div className="text-muted-foreground">{e.email}</div></td>
                                <td className="px-4 py-3 text-xs">{e.hire_date || "—"}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">{fmtMoney(e.salary, e.currency)}</td>
                                <td className="px-4 py-3"><StatusBadge value={e.status} /></td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(e)} className="p-2 hover:text-primary" data-testid={`edit-employee-${e.id}`}><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(e.id)} className="p-2 hover:text-primary" data-testid={`delete-employee-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-muted-foreground">No employees found.</td></tr>}
                    </tbody>
                </table>
            </div>
            {editing && <EmployeeForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function Kpi({ label, value, testId }) {
    return (
        <div className="bg-white border border-border p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-display text-2xl font-bold mt-1">{value}</div>
        </div>
    );
}
